import { Controller } from "@hotwired/stimulus"

// Display/preferences dropdown (extensible).
//
// Persists preferences in localStorage under keys like:
// - wittle:pref:theme => "light" | "dark" | "auto"
//
// Theme is applied via Bootstrap 5.3's recommended data attribute approach:
//   document.documentElement.dataset.bsTheme = "light" | "dark"
export default class extends Controller {
  static targets = ["option"]

  connect() {
    this._mql = window.matchMedia?.("(prefers-color-scheme: dark)") ?? null
    this._onSystemThemeChange = () => {
      if (this.getPref("theme") === "auto") this.applyTheme("auto")
    }

    if (this._mql?.addEventListener) this._mql.addEventListener("change", this._onSystemThemeChange)

    // Ensure explicit defaults so we have a stable baseline for future preferences.
    if (!this.getPref("theme")) this.setPref("theme", "auto")

    this.syncUI()
  }

  disconnect() {
    if (this._mql?.removeEventListener) this._mql.removeEventListener("change", this._onSystemThemeChange)
  }

  choose(event) {
    const pref = event?.currentTarget?.dataset?.pref
    const value = event?.currentTarget?.dataset?.value
    if (!pref || !value) return

    this.setPref(pref, value)
    this.applyPref(pref, value)
    this.syncUI()
  }

  syncUI() {
    const prefs = new Set(this.optionTargets.map((el) => el.dataset.pref).filter(Boolean))

    for (const pref of prefs) {
      const value = this.getPref(pref)
      if (value) this.applyPref(pref, value)

      this.optionTargets
        .filter((el) => el.dataset.pref === pref)
        .forEach((el) => {
          const isActive = el.dataset.value === value
          el.classList.toggle("active", isActive)
          el.setAttribute("aria-pressed", isActive ? "true" : "false")
        })
    }
  }

  applyPref(pref, value) {
    if (pref === "theme") return this.applyTheme(value)

    // Future preferences (font size, font family, etc) can be applied here.
    // Intentionally a no-op for now.
    return undefined
  }

  applyTheme(theme) {
    const resolved = theme === "auto" ? this.getSystemTheme() : theme
    if (resolved !== "light" && resolved !== "dark") return
    document.documentElement.dataset.bsTheme = resolved
  }

  getSystemTheme() {
    return this._mql?.matches ? "dark" : "light"
  }

  storageKey(pref) {
    return `wittle:pref:${pref}`
  }

  getPref(pref) {
    // Back-compat: previously stored as wittle:theme
    if (pref === "theme") {
      const legacy = this._safeGet("wittle:theme")
      if (legacy && !this._safeGet(this.storageKey(pref))) {
        this._safeSet(this.storageKey(pref), legacy)
      }
    }

    return this._safeGet(this.storageKey(pref))
  }

  setPref(pref, value) {
    this._safeSet(this.storageKey(pref), value)
  }

  _safeGet(key) {
    try {
      return window.localStorage?.getItem(key)
    } catch {
      return null
    }
  }

  _safeSet(key, value) {
    try {
      window.localStorage?.setItem(key, value)
    } catch {
      // ignore (private mode / blocked storage)
    }
  }
}



